import rough from 'roughjs';
import type { RoughCanvas } from 'roughjs/bin/canvas';
import type { Options as RoughOptions } from 'roughjs/bin/core';
import { getStroke } from 'perfect-freehand';
import type { CanvasElement } from '@extapia/shared';

// Create a persistent rough generator bound to a canvas.
export const createRoughGen = (canvas: HTMLCanvasElement): RoughCanvas => rough.canvas(canvas);

const toRoughOptions = (el: CanvasElement): RoughOptions => {
  const opts: RoughOptions = {
    seed: el.seed,
    roughness: el.roughness,
    strokeWidth: el.strokeWidth,
    stroke: el.strokeColor,
    fillStyle:
      el.fillStyle === 'solid'
        ? 'solid'
        : el.fillStyle === 'hachure'
        ? 'hachure'
        : el.fillStyle === 'cross-hatch'
        ? 'cross-hatch'
        : el.fillStyle === 'zigzag'
        ? 'zigzag'
        : 'dashed',
    hachureAngle: -41,
    hachureGap: el.strokeWidth * 4,
  };
  if (el.fillColor && el.fillColor !== 'transparent') opts.fill = el.fillColor;
  if (el.strokeStyle === 'dashed') opts.strokeLineDash = [8, 4];
  else if (el.strokeStyle === 'dotted') opts.strokeLineDash = [2, 4];
  return opts;
};

// Draw a single element into the given rough canvas, respecting element
// rotation & opacity. `ctx` is the 2D context of the same canvas.
export const drawElement = (
  ctx: CanvasRenderingContext2D,
  rc: RoughCanvas,
  el: CanvasElement,
): void => {
  if (el.isDeleted) return;

  ctx.save();
  ctx.globalAlpha = Math.max(0, Math.min(1, el.opacity / 100));

  // Rotate around element center
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  ctx.translate(cx, cy);
  ctx.rotate(el.angle);
  ctx.translate(-cx, -cy);

  const opts = toRoughOptions(el);

  switch (el.type) {
    case 'rect': {
      rc.rectangle(el.x, el.y, el.width, el.height, opts);
      break;
    }
    case 'ellipse': {
      rc.ellipse(el.x + el.width / 2, el.y + el.height / 2, el.width, el.height, opts);
      break;
    }
    case 'diamond': {
      const midX = el.x + el.width / 2;
      const midY = el.y + el.height / 2;
      rc.polygon(
        [
          [midX, el.y],
          [el.x + el.width, midY],
          [midX, el.y + el.height],
          [el.x, midY],
        ],
        opts,
      );
      break;
    }
    case 'line':
    case 'arrow': {
      const pts = el.points;
      if (pts.length < 2) break;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        rc.line(el.x + a[0], el.y + a[1], el.x + b[0], el.y + b[1], opts);
      }
      if (el.type === 'arrow' && el.endArrowhead) {
        drawArrowhead(ctx, el.x + pts[pts.length - 1]![0], el.y + pts[pts.length - 1]![1], el.strokeColor, el.strokeWidth);
      }
      break;
    }
    case 'freehand': {
      const stroke = getStroke(
        el.points.map((p) => [el.x + p[0], el.y + p[1], p[2] ?? 0.5]),
        {
          size: el.strokeWidth * 3,
          thinning: 0.6,
          smoothing: 0.5,
          streamline: 0.5,
          simulatePressure: el.simulatePressure,
        },
      );
      if (stroke.length < 2) break;
      ctx.beginPath();
      const first = stroke[0]!;
      ctx.moveTo(first[0]!, first[1]!);
      for (let i = 1; i < stroke.length; i++) {
        const pt = stroke[i]!;
        ctx.lineTo(pt[0]!, pt[1]!);
      }
      ctx.closePath();
      ctx.fillStyle = el.strokeColor;
      ctx.fill();
      break;
    }
    case 'text': {
      const fontMap: Record<number, string> = {
        1: '"Excalifont", "Virgil", cursive',
        2: '"Nunito", sans-serif',
        3: '"Kalam", cursive',
        4: '"Cascadia Code", monospace',
      };
      ctx.font = `${el.fontSize}px ${fontMap[el.fontFamily] ?? 'sans-serif'}`;
      ctx.fillStyle = el.strokeColor;
      ctx.textBaseline = 'top';
      ctx.textAlign = el.textAlign === 'center' ? 'center' : el.textAlign === 'right' ? 'right' : 'left';
      const lines = el.text.split('\n');
      const lineHeight = el.fontSize * el.lineHeight;
      const anchorX =
        el.textAlign === 'center' ? el.x + el.width / 2 : el.textAlign === 'right' ? el.x + el.width : el.x;
      lines.forEach((line, i) => {
        ctx.fillText(line, anchorX, el.y + i * lineHeight);
      });
      break;
    }
    case 'sticky': {
      const colors: Record<string, string> = {
        yellow: '#fef08a',
        pink: '#fbcfe8',
        green: '#bbf7d0',
        blue: '#bfdbfe',
        purple: '#e9d5ff',
      };
      ctx.fillStyle = colors[el.colorScheme] ?? colors.yellow!;
      ctx.fillRect(el.x, el.y, el.width, el.height);
      // Text on top
      ctx.font = `16px "Nunito", sans-serif`;
      ctx.fillStyle = '#111827';
      ctx.textBaseline = 'top';
      const lines = el.text.split('\n');
      lines.forEach((line, i) => {
        ctx.fillText(line, el.x + 10, el.y + 10 + i * 20);
      });
      break;
    }
    case 'image': {
      // Placeholder — actual image drawing needs preloaded HTMLImageElement.
      ctx.strokeStyle = '#94a3b8';
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(el.x, el.y, el.width, el.height);
      ctx.setLineDash([]);
      break;
    }
    case 'frame': {
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth = 1;
      ctx.strokeRect(el.x, el.y, el.width, el.height);
      ctx.font = '12px "Nunito", sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.textBaseline = 'bottom';
      ctx.fillText(el.name, el.x + 4, el.y - 4);
      break;
    }
    default:
      // exhaustiveness check
      break;
  }

  ctx.restore();
};

const drawArrowhead = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  color: string,
  size: number,
): void => {
  const s = size * 4;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - s, y - s / 2);
  ctx.lineTo(x - s, y + s / 2);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
};
